import { useRouteMatch } from '@react-router';
import { useCatalogDepartments } from '../useCatalogDepartments/useCatalogDepartments';
import { useCatalogRoutePaths } from '@apps/registry/common/components/Catalog/Catalog.routes';
import { useCatalogRouting } from '../../useCatalogRouting';
import { UseCatalogStateReturn } from '../../useCatalogState';
import { CatalogDepartmentDictionaryEntry } from '../../state/Catalog';

type UseUpdateSelectedCategoryOnNavigateArgs = Pick<UseCatalogStateReturn, 'selectedCategory'> & {
  updateSelectedCategory: (selectedCategory: CatalogDepartmentDictionaryEntry) => void;
  removeSelectedCategory: () => void;
};

/**
 * Keep track of the current catalog category as user navigates around.
 * There are multiple ways for a user to end up a catalog item (from category, from curations -> category)
 */
export const useUpdateSelectedCategoryOnNavigate = ({ removeSelectedCategory, selectedCategory, updateSelectedCategory }: UseUpdateSelectedCategoryOnNavigateArgs) => {
  const { catalogRoutes } = useCatalogRoutePaths();
  const { catalogCategoryDictionary } = useCatalogDepartments({ selectedCategory });
  const { getPublicPathFromContextualUrl } = useCatalogRouting();

  const categoryRouteMatch = useRouteMatch<{ eventHandle?: string; elementId: string }>([...catalogRoutes.catalogCategory.paths, ...catalogRoutes.brandsList.paths]);
  const catalogIndexRouteMatch = useRouteMatch<{ eventHandle?: string }>({ path: catalogRoutes.catalogIndex.paths, exact: true });
  const curationsIndexRouteMatch = useRouteMatch<{ eventHandle?: string }>(catalogRoutes.curationsIndex.paths);

  // Check if we're on any catalog-related route
  const isOnCatalogRoute = categoryRouteMatch || catalogIndexRouteMatch || curationsIndexRouteMatch;

  if (categoryRouteMatch) {
    // Is department/category/subcategory route

    if (catalogCategoryDictionary) {
      const url = getPublicPathFromContextualUrl(categoryRouteMatch.url);
      const nextCategory = catalogCategoryDictionary[url];

      if (nextCategory && selectedCategory?.id !== nextCategory.id) {
        updateSelectedCategory?.(nextCategory);
      }
    }
  } else if (catalogIndexRouteMatch || curationsIndexRouteMatch) {
    // Is route that doesn't use category data
    if (selectedCategory) {
      removeSelectedCategory();
    }
  } else if (!isOnCatalogRoute && selectedCategory) {
    // We're not on any catalog route, so clear the selected category
    removeSelectedCategory();
  }
};
